/* eslint-disable require-atomic-updates */
// One in-flight batch; live queue is merged only after await.
// Deliberately independent of Vue, chart rendering and business requests.
export const IDLE_MS = 120000;
export const MAX_QUEUE = 200;
const bytes = text => new Blob([text]).size;
export function createUsageProfiler({ send, now = () => Date.now(), id = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}, experience, autoFlush = false }) {
  const session = id();
  let queue = [], sequence = 0, stopped = false, inFlight = false, failures = 0, retryAt = 0;
  let visible = true, lastActivity = now(), lastTick = now(), state = {};
  let pressureTimer = null;
  const panels = new Map();
  const settings = new Map();
  function track(event, surface = 'workspace', tool = '', data = {}, elapsed = 0) {
    if (stopped) return;
    try {
      const row = { id: `${session}-${++sequence}`, session, experience, event, surface, tool,
        at: now(), elapsed: Math.round(elapsed), data };
      // Avoid large state snapshots retaining objects or filling a keepalive request.
      const encoded = JSON.stringify(row);
      if (bytes(encoded) > 12000) return;
      if (queue.length >= MAX_QUEUE) queue.shift();
      queue.push(JSON.parse(encoded));
      // Yield before networking; a burst of setting/exposure events should not
      // evict the initial query while waiting for the regular 30-second flush.
      if (autoFlush && queue.length >= 100 && pressureTimer === null) {
        pressureTimer = setTimeout(() => { pressureTimer = null; flush(); }, 0);
      }
      if (event === 'state') {
        const groups = {
          visualization: ['view', 'theme', 'resolution', 'chart_type', 'volume', 'bar_colors'],
          collection: ['source', 'query_tool', 'sort', 'crypto'],
          portfolio: ['portfolio_currency', 'portfolio_risk', 'portfolio_strategy']
        };
        const setting = (group, key, value) => {
          const signature = JSON.stringify(value), name = `${group}:${key}`;
          if (settings.get(name) === signature) return;
          settings.set(name, signature); track('setting', group, key, { state: value });
        };
        Object.keys(groups).forEach(group => groups[group].forEach(key => {
          if (Object.prototype.hasOwnProperty.call(data, key)) setting(group, key, data[key]);
        }));
        ['filters', 'filter_draft', 'overlays'].forEach(group => {
          if (!data[group]) return;
          // Emit explicit removal, so clearing filters remains queryable.
          const previous = Array.from(settings.keys()).filter(key => key.startsWith(group + ':')).map(key => key.slice(group.length + 1));
          new Set([...previous, ...Object.keys(data[group])]).forEach(key => setting(group, key, data[group][key] === undefined ? null : data[group][key]));
        });
      }
    } catch (_) { /* Telemetry must never break a product action. */ }
  }
  function tick() {
    const time = now();
    // A throttled/suspended tab must never manufacture hours of engagement.
    const elapsed = visible ? Math.max(0, Math.min(time, lastActivity + IDLE_MS) - lastTick) : 0;
    const active = Math.min(elapsed, 30000);
    lastTick = time;
    if (active > 0) {
      track('active', 'workspace', '', {}, active);
      if (state.query_tool) track('tool_time', 'query', state.query_tool, {}, active);
      panels.forEach((tool, surface) => track('tool_time', surface, tool, {}, active));
      const surface = state.view === 'table' ? 'table' : 'header';
      const fields = state[surface === 'table' ? 'table_fields' : 'metric_fields'] || [];
      fields.forEach(field => track('tool_time', surface, field, {}, active));
    }
  }
  function activity() { if (!stopped) { if (now() - lastActivity >= IDLE_MS) tick(); lastActivity = now(); } }
  function visibility(value) {
    tick(); visible = value;
    if (value) lastActivity = now();
    track(value ? 'resume' : 'pause');
  }
  function panel(surface, tool) {
    if ((panels.get(surface) || '') === (tool || '')) return;
    tick();
    if (panels.has(surface)) track('panel_close', surface, panels.get(surface));
    panels.delete(surface);
    if (tool) { panels.set(surface, tool); track('panel_open', surface, tool); }
  }
  function snapshot(next) {
    const changed = {};
    Object.keys(next).forEach(key => {
      if (JSON.stringify(next[key]) !== JSON.stringify(state[key])) changed[key] = next[key];
    });
    if (Object.keys(changed).length) {
      if (changed.metric_fields || changed.table_fields || changed.view || changed.query_tool) tick();
      ['metric_fields', 'table_fields'].forEach(key => {
        if (!Object.prototype.hasOwnProperty.call(changed, key)) return;
        const before = state[key] || [], after = next[key] || [];
        after.filter(field => !before.includes(field)).forEach(field => track('preference', key === 'metric_fields' ? 'header' : 'table', field, { state: 'visible' }));
        before.filter(field => !after.includes(field)).forEach(field => track('preference', key === 'metric_fields' ? 'header' : 'table', field, { state: 'hidden' }));
      });
      state = JSON.parse(JSON.stringify(next));
      track('state', 'workspace', '', changed);
    }
  }
  async function flush(keepalive = false) {
    if (inFlight || !queue.length || (!keepalive && now() < retryAt)) return;
    const batch = [];
    let size = 0;
    while (queue.length && batch.length < 30 && size + bytes(JSON.stringify(queue[0])) < 45000) {
      const row = queue.shift(); size += bytes(JSON.stringify(row)); batch.push(row);
    }
    inFlight = true;
    try { await send(batch, keepalive); failures = 0; }
    catch (_) {
      failures++;
      // Bounded retries; no storage, alerts, or application-wide error interceptor.
      if (!stopped && failures <= 3) queue = batch.concat(queue).slice(-MAX_QUEUE);
      retryAt = now() + Math.min(120000, 10000 * Math.pow(2, failures));
    } finally { inFlight = false; }
    if (!failures && queue.length) return flush(keepalive);
  }
  function stop({ discard = false } = {}) {
    if (stopped) return;
    clearTimeout(pressureTimer); pressureTimer = null;
    if (discard) { stopped = true; queue = []; return; }
    tick();
    panels.forEach((tool, surface) => track('panel_close', surface, tool));
    track('session_end');
    stopped = true; clearTimeout(pressureTimer); pressureTimer = null;
    return flush(true);
  }
  track('session_start');
  return { track, tick, activity, visibility, panel, snapshot, flush, stop };
}
